"use client";

import React, { useMemo } from "react";
import Link from "@/components/Link";
import { Box } from "@dxp-feed/ui/Box";
import { Text } from "@dxp-feed/ui/Text";
import { SiteContainer } from "@/components/Container";
import useMediaQuery from "@/utils/hooks/useMediaQuery";
import { addDataLayer } from "@/helpers/dataLayer";
import ImageWrapper from "@/components/ImageWrapper";
import { Accordion, AccordionItem, AccordionHeader, AccordionPanel, Button } from "@dxp-feed/ui";
function FooterMenu({ footerData }: any) {
  const windowWidth = useMediaQuery();
  const isMobile = useMemo(() => windowWidth !== null && windowWidth <= 1023, [windowWidth]);

  const handleLinkClick = (label: string, link: string) => {
    addDataLayer({
      event: "nav_footer",
      click_text: label,
      click_url: link,
      page_title: document?.title,
    });
  };

  const renderAccordionMenu = () => (
    <Accordion>
      {/* Horizontal List Section */}
      <AccordionItem id="customer-service" className="border-grey350 border-b-[0.5px]">
        <AccordionHeader id="customer-service" className="acccordian-header py-[16px] !px-0">
          {footerData?.customerServiceTitle}
        </AccordionHeader>
        <AccordionPanel id="customer-service" className="p-0 accordian-body">
          <Text as="ul" className="p-0">
            {footerData?.horizontalList?.map((item: any, i: number) => (
              <Text as="li" key={item?.label + i}>
                <Link
                  href={item?.link ?? "#"}
                  className="text-body"
                  target={item?.target}
                  onClick={() => handleLinkClick(item?.label, item?.link ?? "#")}
                >
                  {item?.label}
                </Link>
              </Text>
            ))}
          </Text>
        </AccordionPanel>
      </AccordionItem>

      {/* Vertical List Section */}
      {footerData?.verticalList?.map((menuitem: any, i: number) => (
        <AccordionItem id={menuitem?.heading + i} key={menuitem?.heading + i} className="border-grey350 border-b-[0.5px]">
          <AccordionHeader id={menuitem?.heading + i} className="acccordian-header py-[16px] !px-0">
            {menuitem?.heading}
          </AccordionHeader>
          <AccordionPanel id={menuitem?.heading + i} className="p-0 accordian-body">
            <Text as="ul" className="p-0">
              {menuitem?.verticalLinks?.map((link: any, k: number) => (
                <Text as="li" key={link?.label + k}>
                  <Link
                    href={link?.link ?? "#"}
                    className="text-body"
                    target={link?.target}
                    onClick={() => handleLinkClick(link?.label, link?.link ?? "#")}
                  >
                    {link?.label}
                  </Link>
                </Text>
              ))}
            </Text>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );

  const renderDesktopMenu = () => (
    <>
      {footerData?.verticalList?.map((item: any, i: number) => (
        <Box as="div" className="lg:col-span-1 hidden-tab" key={item?.heading + i}>
          <main>
            <Text as="p" className="text-brandBlack text-body font-semibold leading-normal mb-3">
              {item?.heading}
            </Text>
          </main>
          <nav>
            <Text as="ul" className="[&>li>a:hover]:text-brandSecondary">
              {item?.verticalLinks?.map((innerItem: any, k: number) => (
                <Text as="li" key={innerItem?.label + k}>
                  <Link
                    href={innerItem?.link ?? "#"}
                    className="inline-block text-brandBlack text-body font-normal leading-normal mb-0 pt-1 pb-1 hover:underline"
                    target={innerItem?.target}
                    aria-label={innerItem?.label}
                    onClick={() => handleLinkClick(innerItem?.label, innerItem?.link ?? "#")}
                  >
                    {innerItem?.label}
                  </Link>
                </Text>
              ))}
            </Text>
          </nav>
        </Box>
      ))}
    </>
  );

  return (
    <Box as="div" className="pb-[48px] lg:pb-[40px] pt-[0] lg:pt-[40px] bg-brandWhite" role="navigation">
      <SiteContainer>
        <Box as="div" className="grid-cols-1 lg:grid-cols-5 grid">
          {isMobile ? renderAccordionMenu() : renderDesktopMenu()}

          <Box as="div" className="col-span-1 pt-[0px] lg:pt-0">
            <main>
              <Text as="p" className="border-t-[0.5px] border-grey350 pt-[15px] lg:pt-[0px] lg:border-none text-brandBlack text-body font-semibold leading-normal mb-2">
                {footerData?.stayintheloop}
              </Text>
              <Text as="p" className="text-grey900 text-sm font-normal leading-normal mb-4">
                {footerData?.stayintheloopdesc}
              </Text>

              <Button variant="primary" asChild>
                <Link
                  href={footerData?.newsletterlink ?? "#"}
                  target={footerData?.newsletterlinktarget}
                  className="font-normal border-grey350 border-[1px] !text-body  px-[24px]"
                  aria-label="Join our newsletter"
                  onClick={() => handleLinkClick(footerData?.newslettertitle, footerData?.newsletterlink ?? "#")}
                >
                  {footerData?.newslettertitle}
                </Link>
              </Button>

              <Text as="p" className="text-brandBlack text-body font-semibold leading-normal mt-6 pt-[16px] lg:pt-[0] border-grey350 border-t-[0.5px] lg:border-t-[0px]">
                {footerData?.appstitle}
              </Text>
            </main>

            <Box as="div" className="flex flex-row gap-3 pt-4">
              <Link
                href={footerData?.appleappstorelink}
                aria-label={footerData?.appleappstoreimagealt}
                target={footerData?.appleappstorelinktarget}
                onClick={() => handleLinkClick("iOS", footerData?.appleappstorelink)}
              >
                <ImageWrapper
                  src={`${process.env.NEXT_PUBLIC_AEM_PATH_URL}${footerData?.appleappstoreimage || ""}`}
                  width={107}
                  height={34}
                  alt={footerData?.appleappstoreimagealt}
                />
              </Link>
              <Link
                href={footerData?.googleplaystorelink}
                target={footerData?.googleplaystorelinktarget}
                aria-label={footerData?.googleplaystoreimagealt}
                onClick={() => handleLinkClick("Android", footerData?.googleplaystorelink)}
              >
                <ImageWrapper
                  src={`${process.env.NEXT_PUBLIC_AEM_PATH_URL}${footerData?.googleplaystoreimage || ""}`}
                  width={107}
                  height={34}
                  alt={footerData?.googleplaystoreimagealt}
                />
              </Link>
            </Box>

            <Box as="div" className="text-brandBlack text-body font-semibold leading-normal mt-4">
              <Link
                href={footerData?.learnmorelink}
                target={footerData?.learnmorelinktarget}
                className="text-body font-bold leading-normal mt-4 underline hover:no-underline text-brandSecondary py-3"
                aria-label={footerData?.learnmoretitle}
                onClick={() => handleLinkClick(footerData?.learnmoretitle, footerData?.learnmorelink ?? "#")}
              >
                {footerData?.learnmoretitle}
              </Link>
            </Box>
          </Box>
        </Box>

        <Box as="div" className="copyright-footer border-t-[0.5px] lg:border-t border-grey350 lg:border-[#C4C4C4] pt-5 mt-5 lg:pt-6 lg:mt-6">
          <Box as="div" className="lg:flex flex-row items-center justify-between">
            <Text as="p" className="text-brandBlack text-body font-normal leading-normal lg:w-1/2">
              {footerData?.copyrighttitle}
            </Text>
            <Text as="ul" className="copyright-menu-items flex items-center lg:justify-end pt-[16px] lg:pt-[0] gap-2 2xs:gap-4 lg:gap-[40px] lg:w-1/2 [&>li>a:hover]:text-brandSecondary">
              {footerData?.additionalLinks?.map((item: any, i: number) => (
                <Text as="li" key={item.label + i}>
                  {i === 0 ? (
                    <Link href={item.link ?? "#"} target={item.target} aria-label={item.label}>
                      <ImageWrapper
                        src="/logo-adchoices.svg"
                        alt="ad choices logo"
                        height={25}
                        width={90}
                        className="max-w-[65px] 3xs:max-w-[75px] 2xs:max-w-[90px]"
                      />
                    </Link>
                  ) : (
                    <Link
                      href={item.link ?? "#"}
                      target={item.target}
                      className="inline-block text-brandBlack text-xs 3xs:text-sm 2xs:text-body hover:underline"
                      aria-label={item.label}
                      onClick={() => handleLinkClick(item.label, item.link ?? "#")}
                    >
                      {item.label}
                    </Link>
                  )}
                </Text>
              ))}
            </Text>
          </Box>
        </Box>
      </SiteContainer>
    </Box>
  );
}

export default React.memo(FooterMenu);
